import { Controller } from "@hotwired/stimulus";
import { get } from "@rails/request.js";
import { setHidden } from "../lib/display_toggle";
import { setButtonLoading } from "../lib/button_loading";
import { buildGlAlert, buildGlAlertDismiss } from "../lib/gl_alert";

export default class extends Controller {
  static targets = [
    "targetPrice",
    "baseMargin",
    "baseLeadPrice",
    "marginInput",
    "leadPrice",
    "priceInput",
    "bumpMarginInput",
    "bumpLeadPrice",
    "summaryLeadPrice",
    "summaryBumpLeadPrice",
    "summarySubscriptionPrice",
    "subscriptionDiscountInput",
    "leadQuality",
    "leadType",
    "calculateBlendedWrapper",
    "calculateBlendedBtn",
    "copyAgedPricesWrapper",
    "copyAgedPricesBtn",
    "duplicateModal",
    "duplicateMessage",
    "duplicateOpenLink",
    "bumpMaster",
    "bumpTier",
  ];

  static values = {
    blendedTargetUrl: String,
    checkDuplicateUrl: String,
    premiumAgedPricesUrl: String,
    excludeId: String,
  };

  connect() {
    this.updateBaseLeadPrice();
    this.updateAllLeadPrices();
    this.updateBumpLeadPrice();
    this.updatePricingSummary();
    this.updateBlendedButton();
    this.updateCopyAgedPricesButton();
    this.syncBumpHeader();
    this.updateZeroValueInputTones();
  }

  // Restrict input to digits, a single decimal point, and optional leading minus
  filterPriceInput(event) {
    const input = event.target;
    let value = input.value;

    // Preserve leading minus sign
    const isNegative = value.startsWith("-");

    // Remove any characters that aren't digits or decimal points
    value = value.replace(/[^\d.]/g, "");

    // Ensure only one decimal point
    const parts = value.split(".");
    if (parts.length > 2) {
      value = parts[0] + "." + parts.slice(1).join("");
    }

    // Limit to 2 decimal places
    if (parts.length === 2 && parts[1].length > 2) {
      value = parts[0] + "." + parts[1].substring(0, 2);
    }

    // Restore minus sign
    if (isNegative) {
      value = "-" + value;
    }

    input.value = value;
    this.updateZeroValueInputTone(input);
  }

  // Handle keydown to prevent invalid characters
  handlePriceKeydown(event) {
    const input = event.target;
    const key = event.key;

    // Allow control keys
    if (
      [
        "Backspace",
        "Delete",
        "Tab",
        "Escape",
        "Enter",
        "ArrowLeft",
        "ArrowRight",
        "Home",
        "End",
      ].includes(key)
    ) {
      return;
    }

    // Allow Ctrl/Cmd combinations (copy, paste, etc.)
    if (event.ctrlKey || event.metaKey) {
      return;
    }

    // Allow digits
    if (/^\d$/.test(key)) {
      return;
    }

    // Allow decimal point only if there isn't one already
    if (key === "." && !input.value.includes(".")) {
      return;
    }

    // Allow minus sign only at the start of input
    if (key === "-" && input.selectionStart === 0 && !input.value.includes("-")) {
      return;
    }

    // Prevent all other keys
    event.preventDefault();
  }

  // Update lead price when target price changes
  updateTargetPrice() {
    this.updateBaseLeadPrice();
    this.updateAllLeadPrices();
    this.updateBumpLeadPrice();
  }

  // Update lead price and dependent prices when base margin changes
  updateBaseMargin() {
    this.updateBaseLeadPrice();
    this.updateAllLeadPrices();
    this.updateBumpLeadPrice();
  }

  // Update the computed Lead Price display (Target Price + Base Margin)
  updateBaseLeadPrice() {
    if (!this.hasBaseLeadPriceTarget) return;

    const targetPrice = this.getTargetPrice();
    const baseMargin = this.getBaseMargin();
    this.baseLeadPriceTarget.textContent = this.formatCurrency(
      targetPrice + baseMargin,
    );
    this.updatePricingSummary();
  }

  // Update lead price when tier margin changes
  updateMargin(event) {
    const marginInput = event.target;
    this.updateZeroValueInputTone(marginInput);
    const row = marginInput.closest("tr");
    const leadPriceCell = row.querySelector(
      '[data-lead-pricing-package-form-target="leadPrice"]',
    );

    if (leadPriceCell) {
      this.updateLeadPriceForRow(marginInput, leadPriceCell);
    }
  }

  // Update all lead prices (called on connect and target price change)
  updateAllLeadPrices() {
    this.marginInputTargets.forEach((marginInput, index) => {
      const leadPriceCell = this.leadPriceTargets[index];
      if (leadPriceCell) {
        this.updateLeadPriceForRow(marginInput, leadPriceCell);
      }
    });
  }

  // Calculate and update lead price for a single row
  updateLeadPriceForRow(marginInput, leadPriceCell) {
    const targetPrice = this.getTargetPrice();
    const baseMargin = this.getBaseMargin();
    const tierMargin = parseFloat(marginInput.value) || 0;

    // Effective Price = Target Price + Base Margin + Tier Margin
    const leadPrice = targetPrice + baseMargin + tierMargin;

    leadPriceCell.textContent = this.formatCurrency(leadPrice);
  }

  updateZeroValueInputTones() {
    this.marginInputTargets.forEach((input) => this.updateZeroValueInputTone(input));
  }

  updateZeroValueInputTone(input) {
    if (!input?.classList) return;
    const value = input.value.trim();
    const isSavedZero = value !== "" && Number.parseFloat(value) === 0;
    input.classList.toggle("gl-zero-value-input", isSavedZero);
  }

  // Update bump lead price display
  updateBumpLeadPrice() {
    if (!this.hasBumpMarginInputTarget || !this.hasBumpLeadPriceTarget) return;

    const targetPrice = this.getTargetPrice();
    const baseMargin = this.getBaseMargin();
    const bumpMargin = parseFloat(this.bumpMarginInputTarget.value) || 0;
    const bumpLeadPrice = this.discountedPrice(targetPrice + baseMargin, bumpMargin);

    // Bump Lead Price = Target Price + Margin - Bump Discount
    this.bumpLeadPriceTarget.innerHTML =
      `<div class="fs-5">${this.formatCurrency(bumpLeadPrice)}</div>` +
      `<small class="gl-form-hint">${this.formatCurrency(targetPrice)} ` +
      `<span class="text-body-secondary">+</span> ${this.formatCurrency(baseMargin)} ` +
      `<span class="text-body-secondary">−</span> ${this.formatCurrency(bumpMargin)} ` +
      `<span class="text-body-secondary">=</span> Bump Lead Price</small>`;
    this.updatePricingSummary();
  }

  updatePricingSummary() {
    const leadPrice = this.getTargetPrice() + this.getBaseMargin();
    const bumpAdjustment = this.hasBumpMarginInputTarget
      ? parseFloat(this.bumpMarginInputTarget.value) || 0
      : 0;
    const subscriptionDiscount = this.hasSubscriptionDiscountInputTarget
      ? parseFloat(this.subscriptionDiscountInputTarget.value) || 0
      : 0;

    if (this.hasSummaryLeadPriceTarget) {
      this.summaryLeadPriceTarget.textContent = this.formatCurrency(leadPrice);
    }

    if (this.hasSummaryBumpLeadPriceTarget) {
      this.summaryBumpLeadPriceTarget.textContent = this.formatCurrency(
        this.discountedPrice(leadPrice, bumpAdjustment),
      );
    }

    if (this.hasSummarySubscriptionPriceTarget) {
      this.summarySubscriptionPriceTarget.textContent = this.formatCurrency(
        this.discountedPrice(leadPrice, subscriptionDiscount),
      );
    }
  }

  discountedPrice(price, discount) {
    return Math.max(price - discount, 0);
  }

  // Check for duplicate package before allowing form submission. Bound to the
  // form's `submit` event so it gates submission regardless of who initiated it
  // (the in-form button on a full-page visit OR the unified modal footer's
  // requestSubmit() in frame mode). Because `_confirmSubmit` re-issues
  // requestSubmit(), the resulting `submit` re-enters here — `_submitConfirmed`
  // is the recursion guard that lets that second pass through untouched.
  async checkDuplicateBeforeSubmit(event) {
    if (this._submitConfirmed) {
      this._submitConfirmed = false;
      return;
    }

    event.preventDefault();

    if (!this.hasCheckDuplicateUrlValue || !this.hasLeadTypeTarget) {
      this._confirmSubmit();
      return;
    }

    const leadType = this.leadTypeTarget.value;
    const leadQuality = this.hasLeadQualityTarget ? this.leadQualityTarget.value : "premium";

    if (!leadType) {
      this._confirmSubmit();
      return;
    }

    try {
      const params = new URLSearchParams({
        lead_type: leadType,
        lead_quality: leadQuality,
      });
      if (this.excludeIdValue) params.append("exclude_id", this.excludeIdValue);

      const response = await get(`${this.checkDuplicateUrlValue}?${params}`, { responseKind: "json" });

      if (response.ok) {
        const data = await response.json;

        if (data.duplicate) {
          this._showDuplicateModal(data.package_name, data.open_path);
          return;
        }
      }
    } catch (error) {
      console.warn("Duplicate check failed, proceeding with form submission:", error)
    }

    this._confirmSubmit();
  }

  // Re-submit the form past the duplicate gate. Sets the recursion guard so the
  // re-entrant `submit` event skips the check and lets Turbo submit natively.
  _confirmSubmit() {
    this._submitConfirmed = true;
    // If requestSubmit() is blocked by HTML5 constraint validation, the browser
    // fires `invalid` (synchronously, capture phase) but never `submit`, so the
    // guard reset in checkDuplicateBeforeSubmit never runs and a later submit
    // would silently skip the duplicate check. Clear the guard on that failure.
    const clearGuard = () => { this._submitConfirmed = false; };
    this.element.addEventListener("invalid", clearGuard, { once: true, capture: true });
    this.element.requestSubmit();
    // On the valid path `invalid` never fires, so remove the listener we just
    // added (requestSubmit ran synchronously above) to avoid leaving an orphan.
    this.element.removeEventListener("invalid", clearGuard, { capture: true });
  }

  _showDuplicateModal(packageName, openPath) {
    if (!this.hasDuplicateModalTarget) return;

    if (this.hasDuplicateMessageTarget) {
      this.duplicateMessageTarget.textContent =
        `A pricing package "${packageName}" already exists for this Lead Type and Quality combination.`;
    }
    if (this.hasDuplicateOpenLinkTarget && openPath) {
      this.duplicateOpenLinkTarget.href = openPath;
    }

    const modal = new window.bootstrap.Modal(this.duplicateModalTarget);
    modal.show();
  }

  // Show/hide the Calculate Target button based on lead quality
  updateBlendedButton() {
    if (!this.hasCalculateBlendedWrapperTarget) return;

    const isBlended = this.hasLeadQualityTarget && this.leadQualityTarget.value === "blended";
    setHidden(this.calculateBlendedWrapperTarget, !isBlended);
  }

  // Show/hide the Copy from Premium button based on lead quality
  updateCopyAgedPricesButton() {
    if (!this.hasCopyAgedPricesWrapperTarget) return;

    const isPremium =
      this.hasLeadQualityTarget &&
      this.leadQualityTarget.value === "premium";
    setHidden(this.copyAgedPricesWrapperTarget, isPremium);
  }

  // Fetch aged prices from the premium package and populate inputs
  async copyAgedPricesFromPremium() {
    if (!this.hasLeadTypeTarget || !this.hasPremiumAgedPricesUrlValue) return;

    const leadType = this.leadTypeTarget.value;

    if (!leadType) {
      this._showAgedPricesAlert("Please select a Lead Type first.");
      return;
    }

    const btn = this.copyAgedPricesBtnTarget;
    setButtonLoading(btn, true);

    try {
      const url = `${this.premiumAgedPricesUrlValue}?lead_type=${encodeURIComponent(leadType)}`;
      const response = await get(url, { responseKind: "json" });

      if (response.ok) {
        const data = await response.json;

        if (data.available) {
          const agedPriceInputs = this.element.querySelectorAll(
            "[data-aged-bucket]",
          );
          agedPriceInputs.forEach((input) => {
            const bucket = input.dataset.agedBucket;
            if (data.aged_prices[bucket] !== undefined) {
              input.value = data.aged_prices[bucket].toFixed(2);
            }
          });
          this._showAgedPricesAlert(
            "Aged prices copied from premium package.",
            "success",
          );
        } else {
          this._showAgedPricesAlert(data.message, "warning");
        }
      } else {
        this._showAgedPricesAlert(
          "Failed to fetch premium aged prices. Please try again.",
          "danger",
        );
      }
    } catch (error) {
      this._showAgedPricesAlert(
        "An error occurred while fetching premium aged prices.",
        "danger",
      );
    } finally {
      setButtonLoading(btn, false);
    }
  }

  // One dismissible gl-alert builder for the form's inline feedback (the aged
  // prices copy and the blended-target calculation). `marker` is the per-slot
  // class the caller uses to replace its previous message; the × is the
  // canonical gl-alert-dismiss (never the raw Bootstrap close button) and removes the
  // alert itself, so no Bootstrap Alert plugin is involved.
  _buildDismissibleAlert(message, type, marker) {
    const existing = this.element.querySelector(`.${marker}`);
    if (existing) existing.remove();

    const msgSpan = document.createElement("span");
    msgSpan.className = "gl-alert-content";
    msgSpan.textContent = message;

    const alert = buildGlAlert({
      tone: type,
      layout: "row",
      className: `${marker} mt-2 mb-0 py-2 small`,
      role: "alert",
      body: [msgSpan],
      action: buildGlAlertDismiss({ onDismiss: () => alert.remove() }),
    });
    return alert;
  }

  _showAgedPricesAlert(message, type = "warning") {
    const alert = this._buildDismissibleAlert(message, type, "aged-prices-alert");

    const section = this.element.querySelector("#aged-prices-section");
    if (section) {
      section.before(alert);
    } else {
      this.element.prepend(alert);
    }
  }

  // Fetch premium/standard targets and calculate blended target price
  async calculateBlendedTarget() {
    if (!this.hasLeadTypeTarget) return;

    const leadType = this.leadTypeTarget.value;

    if (!leadType) {
      this._showBlendedAlert("Please select a Lead Type first.");
      return;
    }

    const btn = this.calculateBlendedBtnTarget;
    setButtonLoading(btn, true);

    try {
      const url = `${this.blendedTargetUrlValue}?lead_type=${encodeURIComponent(leadType)}`;
      const response = await get(url, { responseKind: "json" });

      if (response.ok) {
        const data = await response.json;

        if (data.available) {
          this.targetPriceTarget.value = data.blended_target.toFixed(2);
          this.updateTargetPrice();
          this._showBlendedAlert(
            `Target calculated: 25% of Premium ($${data.premium_target.toFixed(2)}) + 75% of Standard ($${data.standard_target.toFixed(2)}) = $${data.blended_target.toFixed(2)}`,
            "success"
          );
        } else {
          this._showBlendedAlert(data.message, "warning");
        }
      }
    } catch (error) {
      this._showBlendedAlert("An error occurred while calculating the target price.", "danger");
    } finally {
      setButtonLoading(btn, false);
    }
  }

  _showBlendedAlert(message, type = "warning") {
    const alert = this._buildDismissibleAlert(message, type, "blended-target-alert");

    const insertAfter = this.calculateBlendedWrapperTarget.closest(".d-flex") || this.calculateBlendedWrapperTarget;
    insertAfter.after(alert);

  }

  getTargetPrice() {
    if (!this.hasTargetPriceTarget) return 0;
    return parseFloat(this.targetPriceTarget.value) || 0;
  }

  getBaseMargin() {
    if (!this.hasBaseMarginTarget) return 0;
    return parseFloat(this.baseMarginTarget.value) || 0;
  }

  formatCurrency(amount) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  }

  // Master checkbox in the Show Bump Option column header.
  // Flips every row's bump-tier checkbox to match the master state.
  toggleAllBump(event) {
    if (!this.hasBumpTierTarget) return;

    const checked = event.target.checked;
    this.bumpTierTargets.forEach((t) => {
      t.checked = checked;
    });
    // Master was the driver — never show indeterminate after a master click
    if (this.hasBumpMasterTarget) {
      this.bumpMasterTarget.indeterminate = false;
    }
  }

  // Called when an individual row checkbox changes.
  // Recomputes the master checkbox state: checked if all rows are on,
  // unchecked if all are off, indeterminate if mixed.
  syncBumpHeader() {
    if (!this.hasBumpMasterTarget || !this.hasBumpTierTarget) return;

    const checkedCount = this.bumpTierTargets.filter((t) => t.checked).length;
    const total = this.bumpTierTargets.length;

    if (checkedCount === total) {
      this.bumpMasterTarget.checked = true;
      this.bumpMasterTarget.indeterminate = false;
    } else if (checkedCount === 0) {
      this.bumpMasterTarget.checked = false;
      this.bumpMasterTarget.indeterminate = false;
    } else {
      this.bumpMasterTarget.indeterminate = true;
    }
  }
}
